import '../theme.css';
import {Header, Navigation} from '../partials/header';
import { NotLoggedIn } from '../partials/notLoggedIn';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '../app';
import { getGrowthIndex } from '../utils/growIndexCalculator';
import '../calendar.css';

const IS_PRODUCTION = process.env.NODE_ENV === "production";
const BACKEND_URI = IS_PRODUCTION ? process.env.REACT_APP_API_BASE_URI : "http://localhost:5050";

function Calendar() {
    const [selectedDay, setSelectedDay] = useState(null);
    const [selectedSeason, setSelectedSeason] = useState(null);
    const [selectedCropInstance, setSelectedCropInstance] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [plantedCrops, setPlantedCrops] = useState([]);
    const [defaultAg, setDefaultAg] = useState(false);
    const [crops, setCrops] = useState([]);
    const [loading, setLoading] = useState(true);

    const { id } = useParams(); // Gets the ID from URL (/calendar/123)
    const navigate = useNavigate();
    const [calendarName, setCalendarName] = useState("New Farm");
    const [calendarImg, setCalendarImg] = useState("calendarLogos/Farmhouse");
    const [currentCalendarId, setCurrentCalendarId] = useState(null);
    const [calendarImageOptions, setCalendarImageOptions] = useState([]);

    const [calendarMenuOpen, setCalendarMenuOpen] = useState(false);
    const menuRef = useRef(null);
    const { user } = useUser();

    useEffect(() => {
        function handleOutsideClick(e) {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setCalendarMenuOpen(false);
            }
        }
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    const seasons = [
        { name: 'Spring', bgColor: '#f0fdf4', emoji: '🌸', index: 1},
        { name: 'Summer', bgColor: '#fefce8', emoji: '☀️', index: 2 },
        { name: 'Fall', bgColor: '#fff7ed', emoji: '🍂', index: 3 },
        { name: 'Winter', bgColor: '#eff6ff', emoji: '❄️', index: 4 }
    ];

    const fertilizers = [
        { name: 'None', growTimeIndex: 0 },
        { name: 'Speed-Gro', growTimeIndex: 1 },
        { name: 'Deluxe Speed-Gro', growTimeIndex: 2 },
        { name: 'Hyper Speed-Gro', growTimeIndex: 3 },
    ];

    // Fetches crops from database
    // TESTING NEW USEEFFECT FOR CALENDAR LOADING
    useEffect(() => {
        // 1. Fetch Crops first (Keep your existing logic here)
        fetch(`${BACKEND_URI}/crop`)
            .then(res => res.json())
            .then(cropData => {
                setCrops(cropData);
                setLoading(false);

                // 2. CHECK URL ID
                if (id && id !== 'new') {
                    // Load the specific calendar
                    fetch(`${BACKEND_URI}/calendar/${id}`, { credentials: 'include' })
                        .then(res => res.json())
                        .then(calData => {
                            setPlantedCrops(calData.plantedCrops);
                            setCalendarName(calData.name);
                            setCalendarImg(calData.imgLink || 'calendarLogos/Farmhouse');
                            setCurrentCalendarId(id);
                        });
                }
            });
    }, [id]); // Re-run if ID changes

    // Fetch available images for dropdown (from backend endpoint)
    useEffect(() => {
        fetch(`${BACKEND_URI}/images/list`)
            .then(res => res.json())
            .then(data => {
                if (data && data.files) setCalendarImageOptions(data.files);
            })
            .catch(err => console.error('Error fetching image list', err));
    }, []);

    const canCropGrowInSeason = (seasonCategory, seasonIndex) => {
        // 1-Spring, 2-Summer, 3-Fall, 4-Winter, 5-All But Winter, 6-Any Season, 7-Spring-Summer, 8-Summer-Fall
        if (seasonCategory === 6) return true; // Any season
        if (seasonCategory === 5 && seasonIndex !== 4) return true; // All but winter
        if (seasonCategory === 7 && (seasonIndex === 1 || seasonIndex === 2)) return true; // Spring-Summer
        if (seasonCategory === 8 && (seasonIndex === 2 || seasonIndex === 3)) return true; // Summer-Fall
        return seasonCategory === seasonIndex; // Exact season match
    };


    const handleDayClick = (season, day) => {
        setSelectedSeason(season);
        setSelectedDay(day);
        setSelectedCropInstance(null);
    };

    const closeSidebar = () => {
        setSelectedDay(null);
        setSelectedSeason(null);
        setSelectedCropInstance(null);
    };

    const plantCrop = (crop) => {
        if (!selectedSeason || !selectedDay) return;
        
        const newCrop = {
            id: Date.now(),
            season: selectedSeason,
            plantDay: selectedDay,
            crop: crop,
            fertilizer: 'None',
            // THIS IS THE DEFAULT AGRICULTURIST
            agriculturist: defaultAg
        };
        
        setPlantedCrops([...plantedCrops, newCrop]);
        closeSidebar();
    };

    const selectCropInstance = (cropInstance) => {
        setSelectedCropInstance(cropInstance);
        setSelectedDay(null);
        setSelectedSeason(null);
    };

    const updateAgriculturist = (event) => {
        if (!selectedCropInstance) return;
        let toggled = event.target.checked;
        console.log(toggled);

        const updatedCrops = plantedCrops.map(crop => 
            crop.id === selectedCropInstance.id 
                ? { ...crop, agriculturist: toggled }
                : crop
        );
        
        setPlantedCrops(updatedCrops);
        
        // Updates the selected crop instance to reflect the change immediately (Self-Note)
        setSelectedCropInstance({
            ...selectedCropInstance,
            agriculturist: toggled
        });
    }

    const toggleAg = () => {
        setDefaultAg(!defaultAg);
    }

    const updateFertilizer = (fertilizerName) => {
        if (!selectedCropInstance) return;
        
        const updatedCrops = plantedCrops.map(crop => 
            crop.id === selectedCropInstance.id 
                ? { ...crop, fertilizer: fertilizerName }
                : crop
        );
        
        setPlantedCrops(updatedCrops);
        
        // Updates the selected crop instance to reflect the change immediately (Self-Note)
        setSelectedCropInstance({
            ...selectedCropInstance,
            fertilizer: fertilizerName
        });
    };

    const deleteCrop = (cropId) => {
        setPlantedCrops(plantedCrops.filter(crop => crop.id !== cropId));
        setSelectedCropInstance(null);
    };

    // TESTING NEW ROUTES FOR IMPLEMENTING CALENDAR SAVING

    const saveCalendar = async () => {
        if (!user) return;

        let nameToSave = calendarName;
        
        // If it's a new calendar, ask the user to name it
        if (!currentCalendarId) {
            const inputName = prompt("Name your new farm:", "My Farm");
            if (!inputName) return; // Cancelled
            nameToSave = inputName;
            setCalendarName(inputName);
        }

        try {
            const response = await fetch(`${BACKEND_URI}/calendar/save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: user.username,
                    calendarId: currentCalendarId, // Pass ID if it exists (Update), null if new (Create)
                    name: nameToSave,
                    imgLink: calendarImg,
                    plantedCrops: plantedCrops
                }),
                credentials: 'include'
            });

            const data = await response.json();
            
            if (response.ok) {
                alert("Calendar Saved!");
                // If we just created a NEW calendar, update the URL and state
                if (!currentCalendarId) {
                    setCurrentCalendarId(data.calendarId);
                    navigate(`/calendar/${data.calendarId}`); // Change URL without reloading
                }
            }
        } catch (err) {
            console.error(err);
        }
    };

    // --- NEW LOAD FUNCTION ---
    // DEPRECATED - unused currently but keeping in case it ends up being useful again.
    // const loadCalendar = async () => {
    //     const user = JSON.parse(localStorage.getItem("user"));
    //     if (!user) return;

    //     try {
    //         // Note: Ensure your user object has the username property
    //         const response = await fetch(`http://localhost:5050/calendar/load/${user.username}`);
    //         const data = await response.json();
            
    //         if (data.plantedCrops) {
    //             setPlantedCrops(data.plantedCrops);
    //         }
    //     } catch (err) {
    //         console.error("Error loading calendar", err);
    //     }
    // };

    const getGrowthDays = (crop, fertilizerName, agriculturist) => {

        const fertilizer = fertilizers.find(f => f.name === fertilizerName);
        let growthIndex = getGrowthIndex(fertilizer.growTimeIndex, agriculturist)
        return crop.growTime[growthIndex]
    };

const getHarvestDays = (plantedCrop) => {
    const crop = plantedCrop.crop;
    const result = [];

    const growthDays = getGrowthDays(crop, plantedCrop.fertilizer, plantedCrop.agriculturist);
    const plantSeasonIndex = seasons.findIndex(s => s.name === plantedCrop.season);

    // Helper to advance season/day with wraparound
    const normalizeDate = (seasonIndex, day) => {
        while (day > 28) {
            day -= 28;
            seasonIndex = (seasonIndex + 1) % 4;
        }
        return { seasonIndex, day };
    };

    // 1) FIRST HARVEST
    let { seasonIndex, day } = normalizeDate(plantSeasonIndex, plantedCrop.plantDay + growthDays);

    // Stop if the first harvest is already out of season

    result.push({
        season: seasons[seasonIndex].name,
        day,
        seasonIndex,
        isFinal: false
    });

    // 2) REPEATING HARVESTS
    if (!crop.regrowTime) return result; // done (single-harvest crop)

    let nextDay = day + crop.regrowTime;
    let nextSeasonIndex = seasonIndex;
    let loop = true;

    while (loop) {
        ({ seasonIndex: nextSeasonIndex, day: nextDay } =
            normalizeDate(nextSeasonIndex, nextDay));

        // Stop if the crop can no longer grow in this new season
        // OR
        // It's grown an entire year
        let grownAYear = (result[0].seasonIndex === nextSeasonIndex) && (result.length > 28)
        if (!canCropGrowInSeason(crop.seasonCategory, seasons[nextSeasonIndex].index) || grownAYear) {
            if (result.length === 1) { break; }
            loop = false;
        }

        result.push({
            season: seasons[nextSeasonIndex].name,
            day: nextDay,
            seasonIndex: nextSeasonIndex,
            isFinal: false
        });

        // Prepare for next loop
        nextDay += crop.regrowTime;
    }

    // Mark final harvest
    if (result.length > 0) {
        result[result.length - 1].isFinal = true;
    }

    return result;
};


    const willCropDieNextSeason = (plantedCrop) => {
                const harvests = getHarvestDays(plantedCrop);
                const harvestInfo = harvests[0];
        const plantSeasonIndex = seasons.findIndex(s => s.name === plantedCrop.season);
        
        // Check if harvest season is different from plant season
        if (harvestInfo.seasonIndex !== plantSeasonIndex) {
            // Check if crop can grow in harvest season
            const harvestSeasonCategory = seasons[harvestInfo.seasonIndex].index;
            if (!canCropGrowInSeason(plantedCrop.crop.seasonCategory, harvestSeasonCategory)) {
                return true;
            }
        }
        return false;
    };


    const getCropsForDay = (season, day) => {
                return plantedCrops.filter(crop => {
            if (crop.season === season && crop.plantDay === day) {
                return true;
            }
            
            const harvests = getHarvestDays(crop);
            if (harvests.some(h => h.season === season && h.day === day)) {
                return true;
            }
            
            return false;
        });
    };

    const getAvailableCropsForSeason = (seasonName) => {
        const season = seasons.find(s => s.name === seasonName);
        if (!season) return [];
        
        return crops.filter(crop => canCropGrowInSeason(crop.seasonCategory, season.index));
    };

    const willApproachNextSeason = (crop, seasonName, day, fertilizerName) => {
        const growthDays = getGrowthDays(crop, fertilizerName, defaultAg);
        const harvestDay = day + growthDays;
        
        // Check if harvest extends beyond current season
        if (harvestDay > 28) {
            const plantSeasonIndex = seasons.findIndex(s => s.name === seasonName);
            const nextSeasonIndex = (plantSeasonIndex + 1) % 4;
            const nextSeasonCategory = seasons[nextSeasonIndex].index;
            
            // Check if crop can't grow in next season
            if (!canCropGrowInSeason(crop.seasonCategory, nextSeasonCategory)) {
                return true;
            }
        }
        return false;
    };


    const filteredCrops = selectedSeason 
        ? getAvailableCropsForSeason(selectedSeason).filter(crop => 
            crop.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : [];

    const renderCalendar = (season) => {
        const days = Array.from({ length: 28 }, (_, i) => i + 1);
        
        return (
            <div 
                key={season.name}
                className="calendar-container"
                style={{ backgroundColor: season.bgColor }}
            >
                <div className="calendar-header">
                    <span className="season-emoji">
                        <img src={`/images/${season.name}.png`} alt="Season"></img>
                    </span>
                    <h2 className="season-title">{season.name}</h2>
                </div>
                
                <div className="calendar-grid">
                    {days.map(day => {
                        const cropsOnDay = getCropsForDay(season.name, day);
                        
                        return (
                            <button
                                key={day}
                                onClick={() => handleDayClick(season.name, day)}
                                className="calendar-day"
                            >
                                <span className="day-number">{day}</span>
                                <div className="day-content">
                                    {cropsOnDay.map(plantedCrop => {
                                        const harvests = getHarvestDays(plantedCrop);
                                        const isHarvestDay = harvests.some(h => h.season === season.name && h.day === day);
                                        let isFinalHarvest = harvests.some(h => h.isFinal && h.season === season.name && h.day === day);
                                        if (harvests.length < 2) { isFinalHarvest = false }

                                        const isPlantDay = plantedCrop.season === season.name && plantedCrop.plantDay === day;
                                        const isDead = willCropDieNextSeason(plantedCrop) && isHarvestDay;
                                        
                                        return (
                                            <div
                                                key={plantedCrop.id}
                                                className="crop-display"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    selectCropInstance(plantedCrop);
                                                }}
                                            >
                                                {isPlantDay && !isHarvestDay && (
                                                    <div className="crop-image-container">
                                                        <img 
                                                            src={`/images/seeds/${plantedCrop.crop.imgLink}_Seeds.png`}
                                                            alt={plantedCrop.crop.name}
                                                            className={`crop-image harvest ${isDead ? 'crop-dead' : ''}`}
                                                        />
                                                    </div>
                                                )}
                                                {isHarvestDay && !isPlantDay && (
                                                    <div className="crop-image-container">
                                                        <img 
                                                            src={`/images/crops/${plantedCrop.crop.imgLink}.png`}
                                                            alt={plantedCrop.crop.name}
                                                            className={`crop-image harvest ${(isFinalHarvest || isDead) ? 'crop-dead' : ''}`}
                                                        />
                                                        {(isFinalHarvest || isDead) && <div className="dead-marker">✕</div>}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    if (!user) {
        return (
            <div>
                <NotLoggedIn />
            </div>
        );
    }

    if (loading) {
        return (
            <div className="calendar-page">
                <div className="loading">Loading crops...</div>
            </div>
        );
    }

    return (
        <div className="theme-container">
            <Navigation />

            <div className="main-content">
                <Header />

                <div className="calendar-page">
                    <div className={`calendar-main ${(selectedDay || selectedCropInstance) ? 'calendar-main-shrink' : ''}`}>
                        <div className='calendar-header'>
                            <div className='calendar-info'>
                                <div className="dropdown-menu-container">
                                    <div className="btn-group" ref={menuRef} style={{position: 'relative', display: 'inline-block'}}>
                                        <button
                                            className="btn-dropdown-toggle"
                                            style={{background: '#deb886', padding: '0.25rem', 'border-color': '#ce8b33', 'border-width': '0.4rem', cursor: 'pointer', 'border-style': 'solid'}}
                                            
                                            id="dropdown-menu-link"
                                            aria-expanded={calendarMenuOpen}
                                            onClick={() => setCalendarMenuOpen(!calendarMenuOpen)}
                                        >
                                            <img className='calendar-image' src={`/images/${calendarImg}.png`} alt='Calendar Logo'/>
                                        </button>

                                        <div
                                            className="dropdown-menu"
                                            style={{
                                                display: calendarMenuOpen ? 'block' : 'none',
                                                position: 'absolute',
                                                top: '100%',
                                                left: 0,
                                                zIndex: 1000,
                                                background: 'white',
                                                border: '1px solid rgba(0,0,0,0.15)',
                                                padding: '6px'
                                            }}
                                        >
                                            {calendarImageOptions.map(opt => {
                                                const rawLabel = opt.split('/').pop();
                                                const label = rawLabel.replace(/_Seeds$/i, ' Seeds').replace(/_/g, ' ');
                                                return (
                                                    <button
                                                        key={opt}
                                                        className={`dropdown-item${calendarImg === opt ? '-selected' : ''}`}
                                                        onClick={() => { setCalendarImg(opt); setCalendarMenuOpen(false); }}
                                                        style={{display: 'flex', alignItems: 'center', width: '100%', border: 'none', padding: '6px 8px', textAlign: 'left', cursor: 'pointer', 'border-radius': '0.5rem'}}
                                                    >
                                                        <img src={`/images/${opt}.png`} alt={label} style={{height: '24px', marginRight: '8px'}} />
                                                        {label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>

                                <h1 className="page-title">{calendarName}</h1>

                                {/* NEW SAVE BUTTON */}
                                <button onClick={saveCalendar} className="save-button" style={{marginRight: '15px', padding: '5px 10px', cursor: 'pointer'}}>
                                    💾 Save Calendar
                                </button>
                            </div>

                            <span className='defaultag-header'>
                                <label htmlFor='defaultag-toggle'><strong>Global Agriculturist: </strong></label>
                                <input type="checkbox" 
                                    id='defaultag-toggle'
                                    className='defaultag-toggle' 
                                    onChange={() => toggleAg()}
                                    checked={defaultAg}
                                />
                            </span>
                        </div>

                        <div className="calendars-grid">
                            {seasons.map(season => renderCalendar(season))}
                        </div>
                    </div>

                    {selectedDay && selectedSeason && !selectedCropInstance && (
                        <div className="sidebar-calendar">
                            <div className="sidebar-header-calendar">
                                <h3 className="sidebar-title-calendar">
                                    Plant Crop - {selectedSeason} Day {selectedDay}
                                </h3>
                                <button onClick={closeSidebar} className="close-button">x</button>
                            </div>

                            <input
                                type="text"
                                placeholder="Search crops..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />

                            <div className="items-list">
                                {filteredCrops.map(crop => {
                                    const willDie = willApproachNextSeason(crop, selectedSeason, selectedDay, 'None');
                                    const growthDays = crop.growTime[getGrowthIndex(0, defaultAg)]
                                    
                                    return (
                                        <button
                                            key={crop.id}
                                            onClick={() => plantCrop(crop)}
                                            className={`crop-item ${willDie ? 'crop-item-warning' : ''}`}
                                        >
                                            <img 
                                                src={`/images/seeds/${crop.imgLink}_Seeds.png`}
                                                alt={`${crop.name} seed`}
                                                className="crop-item-image"
                                            />
                                            <div className="crop-info">
                                                <span className="crop-name">{crop.name}</span>
                                                <span className="crop-days">{growthDays} days to grow</span>
                                                {willDie && (
                                                    <span className="crop-warning-text">⚠ Approaches Next Season</span>
                                                )}
                                            </div>
                                            <img 
                                                src={`/images/crops/${crop.imgLink}.png`}
                                                alt={crop.name}
                                                className="crop-item-image"
                                            />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {selectedCropInstance && (
                        <div className="sidebar-calendar">
                            <div className="sidebar-header-calendar">
                                <h3 className="sidebar-title-calendar">
                                    {selectedCropInstance.crop.name}
                                </h3>
                                <button onClick={() => setSelectedCropInstance(null)} className="close-button">x</button>
                            </div>

                            <div className="crop-details">
                                <div className="crop-timeline">
                                    <div className="timeline-item">
                                        <img 
                                            src={`/images/seeds/${selectedCropInstance.crop.imgLink}_Seeds.png`}
                                            alt={`${selectedCropInstance.crop.name} seed`}
                                            className="timeline-image"
                                        />
                                        <div>
                                            <div className="timeline-label">Planted</div>
                                            <div className="timeline-day">{selectedCropInstance.season} Day {selectedCropInstance.plantDay}</div>
                                        </div>
                                    </div>
                                    <div className="timeline-arrow">→</div>
                                    <div className="timeline-item">
                                        <img 
                                            src={`/images/crops/${selectedCropInstance.crop.imgLink}.png`}
                                            alt={selectedCropInstance.crop.name}
                                            className={`timeline-image ${willCropDieNextSeason(selectedCropInstance) ? 'crop-dead' : ''}`}
                                        />
                                        <div>
                                            <div className="timeline-label">Harvests</div>
                                                <div className="timeline-day">
                                                    {getHarvestDays(selectedCropInstance)
                                                        .filter(h => !h.isFinal)
                                                        .map((h, i) => (
                                                            <div key={i} className="timeline-harvest-entry">
                                                                {h.season} Day {h.day}
                                                            </div>
                                                        ))
                                                    }
                                                </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className='agriculture-container'>
                                <label htmlFor="agriculture-toggle" className='agriculture-title'>Agriculturist</label>
                                <input type="checkbox" 
                                    id='agriculture-toggle'
                                    className='agriculture-toggle' 
                                    onChange={(event) => updateAgriculturist(event)}
                                    checked={(selectedCropInstance.agriculturist) ? selectedCropInstance.agriculturist : false}
                                />
                            </div>

                            <h4 className="fertilizer-title">Select Fertilizer</h4>

                            <div className="fertilizer-list">
                                {fertilizers.map(fert => {
                                    const currentGrowth = getGrowthDays(selectedCropInstance.crop, selectedCropInstance.fertilizer, selectedCropInstance.agriculturist);
                                    const newGrowth = getGrowthDays(selectedCropInstance.crop, fert.name, selectedCropInstance.agriculturist);
                                    const daysSaved = currentGrowth - newGrowth;
                                    
                                    return (
                                        <button
                                            key={fert.name}
                                            onClick={() => updateFertilizer(fert.name)}
                                            className={`fertilizer-item ${selectedCropInstance.fertilizer === fert.name ? 'fertilizer-selected' : ''}`}
                                        >
                                            <div className="fertilizer-info">
                                                <span className="fertilizer-name">{fert.name}</span>
                                                <span className="fertilizer-days">
                                                    {newGrowth} day{newGrowth !== 1 ? 's' : ''}
                                                    {daysSaved > 0 && selectedCropInstance.fertilizer === 'None' && (
                                                        <span className="fertilizer-bonus"> (-{daysSaved})</span>
                                                    )}
                                                </span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            <button
                                onClick={() => deleteCrop(selectedCropInstance.id)}
                                className="delete-button"
                            >
                                Remove Crop
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Calendar;
